import React from 'react';
import {
  Box, Paper, Typography, Button, Divider, CircularProgress, Alert,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

function IncidentDrawerDetailAdvancedBody({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as any;
  return (
    <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Snooze, tuning, exports, suppressions, diagnostics, and raw API payload for this incident. Collapsed by default so the primary review path stays focused.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {s.canManageIncidentWorkflow && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={s.handleMarkFalsePositive}
            >
              Mark false positive
            </Button>
          )}
          {s.canManageIncidentSuppressions && (
            <Button
              size="small"
              variant="outlined"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={() => s.setSuppressDialogOpen(true)}
            >
              Snooze zone
            </Button>
          )}
          {s.canExportIncidentEvidence && (
            <Button
              size="small"
              variant="outlined"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={s.handleExportEvidence}
            >
              Export evidence
            </Button>
          )}
          {s.canAdjustIncidentThreshold && (
            <Button
              size="small"
              variant="outlined"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={s.handleOpenThresholdTuning}
            >
              Adjust threshold
            </Button>
          )}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Suppression (edge)
        </Typography>
        {s.suppressionsError && <Alert severity="warning" sx={{ mb: 1 }}>{s.suppressionsError}</Alert>}
        {s.suppressionsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">Loading suppressions…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            {s.matchingSuppressions.length > 0 ? (
              s.matchingSuppressions.slice(0, 6).map((row: any) => (
                <Paper key={String(row?.id || Math.random())} variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Suppressed
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Until:{' '}
                    {row?.until_at
                      ? new Date(String(row.until_at)).toLocaleString()
                      : row?.until_epoch
                        ? new Date(Number(row.until_epoch) * 1000).toLocaleString()
                        : '—'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Reason: {String(row?.reason || 'suppressed')}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                Not suppressed for this (Type, Zone, Camera).
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {s.canManageIncidentSuppressions && (
            <Button
              size="small"
              variant="outlined"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={() => s.setSuppressDialogOpen(true)}
            >
              Create suppression…
            </Button>
          )}
          {s.canManageIncidentSuppressions && s.matchingSuppressions.length > 0 && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
              onClick={s.handleClearSuppressionForIncident}
            >
              Clear suppression for this incident
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy}
            onClick={s._loadEdgeSuppressions}
          >
            Refresh
          </Button>
        </Box>
        {s.isPpeIncident && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              PPE missing-zone diagnostics (edge)
            </Typography>
            {s.ppeMissingZoneDiagError && <Alert severity="warning" sx={{ mb: 1 }}>{s.ppeMissingZoneDiagError}</Alert>}
            {s.ppeMissingZoneDiagLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">Loading diagnostics…</Typography>
              </Box>
            ) : s.ppeMissingZoneDiag ? (
              <Paper
                variant="outlined"
                sx={{ p: 1.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre', overflow: 'auto' }}
              >
                {JSON.stringify(s.ppeMissingZoneDiag, null, 2)}
              </Paper>
            ) : (
              <Typography variant="body2" color="textSecondary">No diagnostics available.</Typography>
            )}
          </>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Noise controls (debug)
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {(() => {
            const nc =
              (s.selectedIncident as any)?.envelope_metadata?.noise_controls ||
              (s.selectedIncident as any)?.raw?.envelope_metadata?.noise_controls ||
              null;
            if (!nc) return 'noise_controls: —';
            return `noise_controls: ${JSON.stringify(nc, null, 2)}`;
          })()}
        </Paper>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Raw incident JSON
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
          Full payload as returned by the API (debug / integrations).
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            maxHeight: 360,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre',
          }}
        >
          {JSON.stringify((s.selectedIncident as any).raw || s.selectedIncident, null, 2)}
        </Paper>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(
                  JSON.stringify((s.selectedIncident as any).raw || s.selectedIncident, null, 2)
                );
              } catch {
                // best-effort
              }
            }}
          >
            Copy JSON
          </Button>
        </Box>
    </>
  );
}

export function IncidentDrawerDetailAdvanced({
  scope,
  bare = false,
}: {
  scope: IncidentDrawerDetailScope;
  bare?: boolean;
}) {
  const s = scope as any;
  if (bare) {
    return <IncidentDrawerDetailAdvancedBody scope={scope} />;
  }
  return (
    <Accordion
      data-testid={s.isPageMode ? 'incident-review-advanced' : 'incident-drawer-advanced'}
      expanded={s.drawerAdvancedOpen}
      onChange={(_, exp) => s.setDrawerAdvancedOpen(exp)}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>Advanced details</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <IncidentDrawerDetailAdvancedBody scope={scope} />
      </AccordionDetails>
    </Accordion>
  );
}
